import Route from '@ember/routing/route'
import { service } from '@ember/service'
import type Store from '@ember-data/store'
import type RouterService from '@ember/routing/router-service'
import type SessionService from 'district-ui-client/services/session'
import type ExternalRosteringService from 'district-ui-client/services/external-rostering'
import type SubscriptionTypeRoute from 'district-ui-client/subscription-type/route'
import type School from 'district-ui-client/models/school'
import type SchoolStatistic from 'district-ui-client/models/school-statistic'
import type SchoolRollover from 'district-ui-client/models/school-rollover'
import { hasId } from 'district-ui-client/utils/type-guards'

export default class ManageRosterRoute extends Route {
  @service store!: Store

  @service session!: SessionService

  @service router!: RouterService

  @service externalRostering!: ExternalRosteringService

  /**
   * Districts with an external integration (Clever/ClassLink/FTP/Wonde) manage rostering externally,
   * so the internal 3P Roster page is hidden from them - redirect direct URL access to the home page.
   */
  beforeModel() {
    if (this.externalRostering.isEnabled) {
      const { subscriptionType } = this.modelFor('subscription-type') as ModelFor<SubscriptionTypeRoute>
      void this.router.transitionTo('subscription-type.index', subscriptionType)
    }
  }

  async model() {
    const { subscriptionType } = this.modelFor('subscription-type') as ModelFor<SubscriptionTypeRoute>
    const districtId = this.session.currentDistrict.id

    const [schools, schoolRollovers] = await Promise.all([
      this.store.query<School>('school', {
        scope: `districts/${districtId}`,
        // @ts-expect-error - include types not correct
        include: [
          'contact-detail',
          'subscription-coordinators',
          'subscription-coordinators.teacher',
          'school-subscription-utilisations',
        ].join(','),
      }),
      this.store.query<SchoolRollover>('school-rollover', {
        scope: `districts/${districtId}`,
        // @ts-expect-error - include types not correct
        include: 'school',
      }),
    ])

    const activeSchools = schools.filter((school) => school.utilisationFor(subscriptionType)).filter(hasId)

    let schoolStats: SchoolStatistic[] = []
    if (activeSchools.length) {
      schoolStats = await this.store.query<SchoolStatistic>('school-statistic', {
        scope: `districts/${districtId}`,
        filter: { 'school-ids': activeSchools.map((s) => s.id).join(',') },
      })
    }

    return { schools: activeSchools, schoolStats, schoolRollovers: [...schoolRollovers], subscriptionType }
  }
}
